import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button, Calendar, Input, Popover, calendarLocaleRu, cn, isoToZonedParts, zonedPartsToIso, } from '@wit-support/ui';
import { FieldShell } from './field-shell.js';
/**
 * Дата и время «по стенным часам» заданного часового пояса.
 *
 * Значение поля — ISO 8601 в UTC (`2026-10-15T10:17:00.000Z`), то есть
 * момент времени; пояс нужен только для ввода и показа: оператор вводит
 * «15.10, 03:17» по Лос-Анджелесу, а в форму уходит однозначный момент.
 *
 * meta:
 * - `widget: 'datetime'` — включает виджет (строковая схема);
 * - `timeZone` — IANA-пояс (`America/Los_Angeles`); по умолчанию пояс браузера;
 * - `timeZoneLabel` — подпись пояса рядом с полем; по умолчанию сам `timeZone`.
 *
 * Перевод «стенные часы ⇄ UTC» — общий из `@wit-support/ui` (`zoned-time`):
 * время в «дыре» перехода на летнее время не существует — значения нет, и
 * под полем сразу сказано почему (`DST_GAP_MESSAGE`), а не общее «укажите».
 */
export const DST_GAP_MESSAGE = 'Такого времени в этот день нет — переход на летнее время. Выберите другое время.';
const pad = (n) => String(n).padStart(2, '0');
function browserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }
    catch {
        return 'UTC';
    }
}
/** `YYYY-MM-DD` → Date в полдень по местному времени (только для календаря). */
function dateFromYmd(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12) : undefined;
}
function ymdFromDate(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function formatYmdRu(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : ymd;
}
export function DateTimeWidget(props) {
    const { field, fieldState, meta, label, description, hint, readonly, disabled } = props;
    const timeZone = typeof meta.timeZone === 'string' ? meta.timeZone : browserTimeZone();
    const tzLabel = typeof meta.timeZoneLabel === 'string' ? meta.timeZoneLabel : timeZone;
    const locked = Boolean(disabled || (readonly ?? meta.readonly));
    const fromValue = React.useCallback((v) => (typeof v === 'string' ? isoToZonedParts(v, timeZone) : null) ?? { date: '', time: '' }, [timeZone]);
    // Черновик: пока заполнена только дата (или только время), значения поля
    // ещё нет — но набранное не должно пропадать.
    const [draft, setDraft] = React.useState(() => fromValue(field.value));
    const [open, setOpen] = React.useState(false);
    const lastEmitted = React.useRef(field.value);
    const lastZone = React.useRef(timeZone);
    React.useEffect(() => {
        // Значение сменили снаружи (reset формы) или сменился пояс при том же
        // моменте — перечитать «стенные» дату и время.
        if (field.value !== lastEmitted.current || timeZone !== lastZone.current) {
            lastEmitted.current = field.value;
            lastZone.current = timeZone;
            setDraft(fromValue(field.value));
        }
    }, [field.value, timeZone, fromValue]);
    const commit = (next) => {
        setDraft(next);
        const iso = next.date && next.time ? zonedPartsToIso(next, timeZone) : null;
        const value = iso ?? undefined;
        lastEmitted.current = value;
        field.onChange(value);
    };
    // Дата и время введены, а момента нет — «дыра» перехода на летнее время.
    const gap = Boolean(draft.date && draft.time) && zonedPartsToIso(draft, timeZone) == null;
    return (_jsx(FieldShell, { meta: meta, fieldState: gap
            ? { ...fieldState, isTouched: true, error: { type: 'dst-gap', message: DST_GAP_MESSAGE } }
            : fieldState, label: label, description: description, hint: hint, htmlFor: field.name, children: _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx(Popover.Trigger, { asChild: true, children: _jsxs(Button, { type: "button", variant: "secondary", id: field.name, disabled: locked, className: cn('gap-2 font-normal', !draft.date && 'text-[var(--color-text-muted)]'), children: [_jsx(CalendarIcon, { "aria-hidden": true, className: "h-4 w-4 opacity-70" }), draft.date ? formatYmdRu(draft.date) : (meta.placeholder ?? 'Дата')] }) }), _jsx(Popover.Content, { className: "w-auto p-0", align: "start", sideOffset: 6, children: _jsx(Calendar, { mode: "single", locale: calendarLocaleRu, selected: dateFromYmd(draft.date), onSelect: (d) => {
                                    if (!d)
                                        return;
                                    commit({ ...draft, date: ymdFromDate(d) });
                                    setOpen(false);
                                } }) })] }), _jsx(Input, { type: "time", "aria-label": "\u0412\u0440\u0435\u043C\u044F", name: `${field.name}__time`, className: "w-32", value: draft.time, disabled: locked, onBlur: field.onBlur, onChange: (e) => commit({ ...draft, time: e.target.value }) }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "datetime-tz", children: tzLabel })] }) }));
}
//# sourceMappingURL=datetime.js.map